import React from 'react';
import { Box } from '@mui/system';
import pxToEm from 'theme/pxToEm';
import { useIsMobileDevice } from 'utils';
import { DidYouMeanWrapper } from './components/DidYouMeanWrapper';
import { RowCategory } from './components/RowCategory';
import { SearchHistory } from './components/SearchHistory';
import { SkeletonContainer } from './components/SkeletonContainer';
import {
  SearchAutocompleteResultsProps,
  ImageVariant,
} from './SearchAutocompleteResults.types';

const DEFAULT_SUGGESTIONS_MAX_COUNT = 5;
const DEFAULT_MOBILE_SUGGESTIONS_MAX_COUNT = 4;
const DEFAULT_MOBILE_ITEMS_MAX_COUNT = 4;

export const SearchAutocompleteResults: React.FC<
  SearchAutocompleteResultsProps
> = ({
  results,
  isResultsOpen = false,
  imgVariant = ImageVariant.SQUARE,
  query = '',
  correctedQuery = '',
  shouldShowHistory = false,
  shouldShowSuggestions = false,
  searchHistory,
  onItemClick,
  isLoading = false,
  shouldShowArrows = false,
  shouldShowImages = false,
  onQueryClick,
  withRowGap = true,
  selectedItemId = null,
  shouldShowDividers = true,
  shouldShowIcons = false,
  shouldShowClearItemButton = false,
  maxItemsCount,
  maxMobileItemsCount = maxItemsCount ?? DEFAULT_MOBILE_ITEMS_MAX_COUNT,
  maxHistoryItemsCount = maxItemsCount ?? DEFAULT_SUGGESTIONS_MAX_COUNT,
  maxMobileHistoryItemsCount = maxMobileItemsCount ??
    DEFAULT_MOBILE_SUGGESTIONS_MAX_COUNT,
  invertQueryHighlighting = false,
  className,
  onClearItem,
  onItemHover,
}) => {
  if (!isResultsOpen) return null;

  const isMobileDevice = useIsMobileDevice();

  const gap = withRowGap ? 24 : 0;

  return (
    <Box
      id="searchAutocompleteResults"
      display="flex"
      flexDirection="column"
      rowGap={pxToEm(gap)}
      marginY={pxToEm(40)}
      className={className}
    >
      {isLoading && (
        <SkeletonContainer
          shouldShowDividers={shouldShowDividers}
          shouldShowImages={shouldShowImages}
        />
      )}
      {shouldShowHistory && !isLoading && (
        <SearchHistory
          searchHistory={searchHistory}
          onItemClick={onItemClick}
          onItemHover={onItemHover}
          query={query}
          shouldShowDividers={shouldShowDividers}
          shouldShowArrows={shouldShowArrows}
          shouldShowClearItemButton={shouldShowClearItemButton}
          maxHistoryItemsCount={maxHistoryItemsCount}
          maxMobileHistoryItemsCount={maxMobileHistoryItemsCount}
          invertQueryHighlighting={invertQueryHighlighting}
          onClearItem={onClearItem}
          selectedItemId={selectedItemId}
        />
      )}
      {shouldShowSuggestions && !isLoading && (
        <>
          {correctedQuery?.length > 0 && (
            <DidYouMeanWrapper
              query={query}
              correctedQuery={correctedQuery}
              onQueryClick={(val) => onQueryClick?.(val)}
            />
          )}
          {results.map(({ title, items }, i) => (
            <RowCategory
              shouldShowArrows={shouldShowArrows}
              key={i}
              title={title}
              items={items}
              imgVariant={imgVariant}
              query={query}
              onItemClick={onItemClick}
              onItemHover={onItemHover}
              shouldShowImages={shouldShowImages}
              selectedItemId={selectedItemId}
              shouldShowDividers={shouldShowDividers}
              shouldShowIcons={shouldShowIcons}
              maxItemsCount={maxItemsCount}
              maxMobileItemsCount={maxMobileItemsCount}
              invertQueryHighlighting={invertQueryHighlighting}
              isMobileDevice={isMobileDevice}
            />
          ))}
        </>
      )}
    </Box>
  );
};
